import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import classNames from "classnames";

import { TextInput } from "../../../components/forms/TextInput/TextInput";
import { Collapse } from "../../../components/Collapse/Collapse";

interface ParamsSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  isGuidelinesGenerated: boolean;
}

export const ParamsSection: React.FC<ParamsSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
  isGuidelinesGenerated,
}) => {
  const { t } = useTranslation();
  const { register } = useFormContext();

  return (
    <Collapse
      title={
        <div className="flex justify-between items-center w-full min-w-0 pr-4">
          <div className="flex items-center gap-2">
            {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
            <span
              className={classNames(
                "text-lg font-bold font-header",
                isLocked ? "text-[#877369]" : "text-[#6c2f00]",
              )}
            >
              {t("Pages:StyleCreator.Params.Title", "4. Technical parameters")}
            </span>
          </div>
          {isGuidelinesGenerated && (
            <span className="px-3 py-1 bg-[#b1f0ce] text-[#0e5138] text-xs font-semibold rounded-full flex items-center gap-1">
              <span className="icon-[ph--check-circle] text-xs" />
              {t("Pages:StyleCreator.Params.Autofilled", "Auto-filled")}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset
        disabled={isLocked}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full"
      >
        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t("Pages:StyleCreator.Params.Og", "Original extract (°Blg)")}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minOg")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxOg")}
              placeholder="Max"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t("Pages:StyleCreator.Params.Fg", "Final extract (°Blg)")}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minFg")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxFg")}
              placeholder="Max"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t("Pages:StyleCreator.Params.Abv", "ABV (%)")}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minAbv")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxAbv")}
              placeholder="Max"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t("Pages:StyleCreator.Params.Ibu", "IBU")}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minIbu")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxIbu")}
              placeholder="Max"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t("Pages:StyleCreator.Params.Color", "Color (EBC)")}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minColor")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxColor")}
              placeholder="Max"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-[#877369] uppercase tracking-wider block mb-2">
            {t(
              "Pages:StyleCreator.Params.ServingTemp",
              "Serving temperature (°C)",
            )}
          </span>
          <div className="flex items-center gap-2">
            <TextInput
              containerClassName="w-full"
              {...register("minServingTemp")}
              placeholder="Min"
            />
            <span className="text-[#877369]">—</span>
            <TextInput
              containerClassName="w-full"
              {...register("maxServingTemp")}
              placeholder="Max"
            />
          </div>
        </div>
      </fieldset>
    </Collapse>
  );
};
